/**
 * Role-based access control.
 *
 * Two roles cover a tool for one seller account. Permissions are enumerated
 * rather than inferred from role comparisons so that adding a role later
 * cannot silently widen access through an ordering assumption: every guard
 * asks `can(role, permission)` by name, never "is this role at least X".
 *
 * Reading is not a permission. Anyone with an account may look at every
 * screen; the permissions are for the things that change something.
 */

export const ROLES = ['admin', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = ['users:manage', 'data:import'] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  viewer: [],
  admin: ['users:manage', 'data:import'],
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  admin: 'Administrator',
  viewer: 'Viewer',
};

export const ROLE_DESCRIPTIONS: Readonly<Record<Role, string>> = {
  admin: 'Can import reports and manage user accounts.',
  viewer: 'Read-only access to every screen.',
};
